import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTag,
  faRocket,
  faStar,
  faPencil,
  faCheck,
  faTimes,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const SCOPE_LABELS = {
  specific_states: "Specific States",
  all_india: "All India",
  nri_specific: "NRI – Specific",
  nri_all: "NRI – All",
};
const DURATION_DAYS = [1, 2, 3, 4, 7];

const fmt = (v) => (v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—");

// ─── Editable price cell ──────────────────────────────────────────────────────
const EditCell = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(Number(val));
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type="number"
          className="w-20 border border-gray-300 rounded px-1 py-0.5 text-sm"
          value={val}
          onChange={(e) => setVal(e.target.value)}
        />
        {saving ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 text-xs" />
        ) : (
          <>
            <button onClick={save} className="text-green-600 hover:text-green-800">
              <FontAwesomeIcon icon={faCheck} className="text-xs" />
            </button>
            <button onClick={() => setEditing(false)} className="text-red-500 hover:text-red-700">
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer group"
      onClick={() => { setVal(value ?? ""); setEditing(true); }}
    >
      <span>{fmt(value)}</span>
      <FontAwesomeIcon
        icon={faPencil}
        className="text-gray-300 group-hover:text-blue-500 text-xs transition"
      />
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const PricingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [savingBoost, setSavingBoost] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get("mst-admin/pricing-dashboard");
      if (res?.status === 200 && res.data?.success) {
        setData(res.data);
      } else {
        toast.error("Failed to load pricing data");
      }
    } catch {
      toast.error("Failed to load pricing data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pack rows are the canonical `price.*` AppConfig keys, whose value shape is
  // { amount, ... }. The app display endpoint, the comparison/quick-card unlock
  // options and the purchase path ALL read `value.amount` — so the admin must
  // write `amount` (not the old `price`) for one edit to reach every surface.
  // Backend merges this into the existing value, preserving currency/validity.
  const updatePackPrice = async (key, price) => {
    try {
      const res = await func.put("mst-admin/pricing-dashboard/item", { key, value: { amount: price }, category: "pricing" });
      if (res?.status === 200 && res.data?.success) {
        toast.success("Price updated");
        fetchData();
      } else {
        toast.error("Failed to update price");
      }
    } catch {
      toast.error("Failed to update price");
    }
  };

  // Misc feature add-ons (e.g. addon.alignment_detail) keep the legacy { price }
  // value shape — getPricingDashboard reads them via `value.price`, not `.amount`.
  const updateMiscAddon = async (key, price) => {
    try {
      const res = await func.put("mst-admin/pricing-dashboard/item", { key, value: { price }, category: "pricing" });
      if (res?.status === 200 && res.data?.success) {
        toast.success("Price updated");
        fetchData();
      } else {
        toast.error("Failed to update price");
      }
    } catch {
      toast.error("Failed to update price");
    }
  };

  const updateBoostPrice = async (scopeType, day, newPrice) => {
    const updated = { ...data.boostPrices };
    updated[scopeType] = { ...updated[scopeType], [day]: newPrice };
    setSavingBoost(true);
    try {
      const res = await func.put("mst-admin/pricing-dashboard/boost-prices", { prices: updated });
      if (res?.status === 200 && res.data?.success) {
        toast.success("Boost price updated");
        fetchData();
      } else {
        toast.error("Failed to update boost price");
      }
    } catch {
      toast.error("Failed to update boost price");
    }
    setSavingBoost(false);
  };

  const updateAddon = async (addonKey, newPrice) => {
    const updated = { ...data.boostAddons, [addonKey]: newPrice };
    try {
      const res = await func.put("mst-admin/pricing-dashboard/boost-prices", { addons: updated });
      if (res?.status === 200 && res.data?.success) {
        toast.success("Add-on price updated");
        fetchData();
      } else {
        toast.error("Failed to update add-on price");
      }
    } catch {
      toast.error("Failed to update add-on price");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-gray-400" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-3 mb-2">
        <FontAwesomeIcon icon={faTag} className="text-2xl text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800">Pricing Dashboard</h1>
        <span className="text-sm text-gray-400 ml-1">Click any price to edit</span>
      </div>

      {/* ── Pack Prices ── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faStar} className="text-yellow-500" />
          Subscription Pack Prices
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b">
                <th className="pb-2 pr-4">Pack</th>
                <th className="pb-2 pr-4">Duration</th>
                <th className="pb-2">Price</th>
              </tr>
            </thead>
            <tbody>
              {(data.packs || []).map((pack) => (
                <tr key={pack.key} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 pr-4 font-medium text-gray-800">{pack.label}</td>
                  <td className="py-3 pr-4 text-gray-500">{pack.duration}</td>
                  <td className="py-3">
                    <EditCell value={pack.price} onSave={(v) => updatePackPrice(pack.key, v)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Boost Prices ── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-1 flex items-center gap-2">
          <FontAwesomeIcon icon={faRocket} className="text-purple-500" />
          Profile Boost Prices
          {savingBoost && <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 text-sm" />}
        </h2>
        <p className="text-xs text-gray-400 mb-4">Rows = scope, Columns = duration days</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b">
                <th className="pb-2 pr-4">Scope</th>
                {DURATION_DAYS.map((d) => (
                  <th key={d} className="pb-2 pr-4">{d}d</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(SCOPE_LABELS).map(([scopeType, label]) => (
                <tr key={scopeType} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-3 pr-4 font-medium text-gray-700">{label}</td>
                  {DURATION_DAYS.map((d) => (
                    <td key={d} className="py-3 pr-4">
                      <EditCell
                        value={data.boostPrices?.[scopeType]?.[d]}
                        onSave={(v) => updateBoostPrice(scopeType, d, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add-ons */}
        <div className="mt-6 border-t pt-4">
          <p className="text-sm font-semibold text-gray-600 mb-3">Boost Add-ons</p>
          <div className="flex gap-8">
            <div>
              <p className="text-xs text-gray-400 mb-1">Match DNA Reveal</p>
              <EditCell
                value={data.boostAddons?.matchDNA}
                onSave={(v) => updateAddon("matchDNA", v)}
              />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">Cosmic Match Reveal</p>
              <EditCell
                value={data.boostAddons?.cosmicMatch}
                onSave={(v) => updateAddon("cosmicMatch", v)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Misc Add-ons ── */}
      {(data.addons || []).length > 0 && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Feature Add-ons</h2>
          <div className="space-y-3">
            {data.addons.map((a) => (
              <div key={a.key} className="flex items-center justify-between border-b pb-2">
                <span className="text-sm text-gray-700">{a.label}</span>
                <EditCell value={a.price} onSave={(v) => updateMiscAddon(a.key, v)} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PricingDashboard;
